import { Capacitor, registerPlugin } from '@capacitor/core'

export interface IBluetoothPrinterDevice {
  name: string
  address: string
}

export interface IUsbPrinterDevice {
  deviceId: string
  /** Stable "vendor:product" in hex — deviceId is reassigned on every replug. */
  deviceKey: string
  deviceName: string
  productName?: string
  manufacturerName?: string
  vendorId: number
  productId: number
  hasPermission: boolean
  /**
   * Whether this plugin can write to the device at all — it needs a bulk OUT
   * endpoint. listUsbDevices reports every attached device, including unusable ones,
   * so an unrecognised printer can be diagnosed rather than silently vanishing; only
   * supported devices may be printed to or auto-selected.
   */
  supported: boolean
  /** Diagnostics, for identifying a model that this plugin does not recognise. */
  deviceClass: number
  interfaceClasses: string
  isPrinterClass: boolean
}

export interface IPrinterStatus {
  bluetoothSupported: boolean
  bluetoothEnabled: boolean
  bluetoothPermissionGranted: boolean
  usbSupported: boolean
}

export interface IPermissionResult {
  bluetooth: 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale'
}

export interface IPrinterIdentity {
  /** The cleaned ~HI reply, always present — shown for diagnosis when parsing fails. */
  raw: string
  model?: string
  firmware?: string
  dotsPerMm?: number
  /** Absent rather than guessed when the reply can't be parsed: a wrong dpi silently rescales every label. */
  dpi?: number
}

export interface ILabelPrinterPlugin {
  getStatus(): Promise<IPrinterStatus>
  checkPermissions(): Promise<IPermissionResult>
  requestPermissions(): Promise<IPermissionResult>
  listBluetoothDevices(): Promise<{ devices: IBluetoothPrinterDevice[] }>
  listUsbDevices(): Promise<{ devices: IUsbPrinterDevice[] }>
  requestUsbPermission(options: { deviceKey: string }): Promise<{ granted: boolean }>
  /** ZPL is passed base64-encoded so the server's exact bytes survive the JSON bridge. */
  printBluetooth(options: { base64: string, deviceName?: string }): Promise<{ deviceName: string }>
  printUsb(options: { base64: string, deviceKey?: string }): Promise<{ deviceName: string }>
  /** Asks the printer what it is via ZPL Host Identification (~HI). */
  identifyPrinter(options: {
    transport: 'usb' | 'bluetooth'
    deviceKey?: string
    deviceName?: string
  }): Promise<IPrinterIdentity>
}

const PLUGIN_NAME = 'LabelPrinter'

const LabelPrinter = registerPlugin<ILabelPrinterPlugin>(PLUGIN_NAME)

/**
 * The plugin ships in the APK while this file ships over OTA, so a bundle that knows
 * about it can land on an older APK where it is absent. Callers check this to give the
 * seller an actionable message rather than Capacitor's generic "not implemented".
 */
export const isLabelPrinterAvailable = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.isPluginAvailable(PLUGIN_NAME)

export default LabelPrinter
